import { useEffect, useRef, useState } from 'react';
import '../styles/orderLookup.css';
import stranLogo from '../styles/stran_logo.png';

import OrderResult from '../components/OrderResult';
import HistoryList from '../components/HistoryList';
import { fetchOrderDetails, fetchOrderHistory, fetchOrderLookup } from '../services/orderApi';
import { resolveLookupParams } from '../utils/lookupParams';

function getOrderSelectionKey(order) {
  return order?.entityId && order?.server ? `${order.server}:${order.entityId}` : null;
}

const OrderLookupPage = () => {
  const initialLookupParams = resolveLookupParams();
  const hasAutoLookedUpRef = useRef(false);
  const [mode, setMode] = useState(initialLookupParams ? 'lookup' : 'history');
  const [lookupEmail, setLookupEmail] = useState(initialLookupParams?.email || '');
  const [lookupOrderNumber, setLookupOrderNumber] = useState(initialLookupParams?.orderNumber || '');
  const [lookupStatus, setLookupStatus] = useState('');
  const [historyEmail, setHistoryEmail] = useState('');
  const [historyPassword, setHistoryPassword] = useState('');
  const [historyStatus, setHistoryStatus] = useState('');
  const [historyOrders, setHistoryOrders] = useState([]);
  const [selectedHistoryOrderId, setSelectedHistoryOrderId] = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);
  const [detailRawOrder, setDetailRawOrder] = useState(null);
  const [detailStatus, setDetailStatus] = useState(
    initialLookupParams ? 'Looking up your order...' : 'Search for an order to view details.'
  );

  async function runLookup(emailValue, orderNumberValue) {
    setLookupStatus('Looking up your order...');
    setDetailStatus('Looking up your order...');
    setDetailOrder(null);
    setDetailRawOrder(null);

    try {
      const data = await fetchOrderLookup({ email: emailValue, orderNumber: orderNumberValue });
      const nextOrder = data?.order;
      const rawOrder = data?.rawOrder || null;

      if (!nextOrder) {
        throw new Error('Order not found.');
      }

      setMode('lookup');
      setDetailOrder(nextOrder);
      setDetailRawOrder(rawOrder);
      setDetailStatus('');
      setLookupStatus('Order found.');
    } catch (err) {
      const message = err.message || 'Order lookup failed';
      setLookupStatus(message);
      setDetailStatus(message);
    }
  }

  async function handleLookupSubmit(e) {
    e.preventDefault();
    await runLookup(lookupEmail, lookupOrderNumber);
  }

  async function handleHistoryOrderSelect(orderSummary) {
    if (!orderSummary?.entityId || !orderSummary?.server) {
      return;
    }

    setSelectedHistoryOrderId(getOrderSelectionKey(orderSummary));
    setDetailStatus(`Loading order ${orderSummary.id}...`);
    setDetailOrder(null);
    setDetailRawOrder(null);

    try {
      const data = await fetchOrderDetails(orderSummary.entityId, orderSummary.server);
      const nextOrder = data?.order;
      const nextRawOrder = data?.rawOrder || null;

      if (!nextOrder) {
        throw new Error('Order details not found.');
      }

      setMode('history');
      setDetailOrder(nextOrder);
      setDetailRawOrder(nextRawOrder);
      setDetailStatus('');
    } catch (err) {
      setDetailStatus(err.message || 'Order details lookup failed');
    }
  }

  async function handleHistorySubmit(e) {
    e.preventDefault();

    if (historyPassword !== historyEmail.split('@')[0]) {
      setHistoryStatus('Password does not match email.');
      return;
    }

    setHistoryStatus('Searching...');
    setSelectedHistoryOrderId(null);
    setHistoryOrders([]);
    setDetailOrder(null);
    setDetailRawOrder(null);
    setDetailStatus('Select an order to view details.');

    try {
      const data = await fetchOrderHistory(historyEmail);
      const orders = Array.isArray(data?.orders) ? data.orders : [];
      setHistoryOrders(orders);
      setHistoryStatus(`Found ${orders.length} order(s).`);

      if (orders.length > 0) {
        await handleHistoryOrderSelect(orders[0]);
      } else {
        setDetailStatus('No orders found for that email.');
      }
    } catch (err) {
      const message = err.message || 'Order history lookup failed';
      setHistoryStatus(message);
      setDetailStatus(message);
    }
  }

  useEffect(() => {
    if (!initialLookupParams || hasAutoLookedUpRef.current) {
      return;
    }

    hasAutoLookedUpRef.current = true;
    runLookup(initialLookupParams.email, initialLookupParams.orderNumber);
  }, [initialLookupParams]);

  return (
    <main className="card dashboard-card">
      <section className="dashboard-top">
        <header className="brand-lockup">
          <img className="brand-logo" src={stranLogo} alt="Stran" />
          <h1>Order Lookup</h1>
          <p>Search for one order or browse your order history.</p>
        </header>

        <nav className="mode-nav" aria-label="Lookup type">
          <button
            type="button"
            className={mode === 'lookup' ? 'mode-tab active' : 'mode-tab'}
            onClick={() => setMode('lookup')}
          >
            Single Order
          </button>
          <button
            type="button"
            className={mode === 'history' ? 'mode-tab active' : 'mode-tab'}
            onClick={() => setMode('history')}
          >
            Order History
          </button>
        </nav>

        <div className="top-panel">
          {mode === 'lookup' ? (
            <form className="top-form" onSubmit={handleLookupSubmit}>
              <label className="field">
                <span>Email</span>
                <input value={lookupEmail} onChange={e => setLookupEmail(e.target.value)} required />
              </label>

              <label className="field">
                <span>Order Number</span>
                <input value={lookupOrderNumber} onChange={e => setLookupOrderNumber(e.target.value)} required />
              </label>

              <div className="form-actions">
                <button type="submit">Look Up Order</button>
                <div className="status-inline">{lookupStatus}</div>
              </div>
            </form>
          ) : (
            <form className="top-form top-form-history" onSubmit={handleHistorySubmit}>
              <label className="field">
                <span>Email</span>
                <input value={historyEmail} onChange={e => setHistoryEmail(e.target.value)} required />
              </label>

              <label className="field">
                <span>Password</span>
                <input value={historyPassword} onChange={e => setHistoryPassword(e.target.value)} required />
              </label>

              <div className="form-actions">
                <button type="submit">Search History</button>
                <div className="status-inline">{historyStatus}</div>
              </div>
            </form>
          )}
        </div>
      </section>

      <section className="dashboard-main">
        <aside className="master-panel">
          <div className="panel-header">
            <h2>{mode === 'history' ? 'Order History' : 'Lookup Panel'}</h2>
            <p>
              {mode === 'history'
                ? 'Select an order to load its full details.'
                : 'Use the top form to search for a specific order by email and order number.'}
            </p>
          </div>

          {mode === 'history' ? (
            <HistoryList
              orders={historyOrders}
              selectedOrderId={selectedHistoryOrderId}
              onSelectOrder={handleHistoryOrderSelect}
            />
          ) : (
            <div className="empty-panel">
              Search results load into the detail panel on the right.
            </div>
          )}
        </aside>

        <section className="detail-panel">
          {detailOrder ? (
            <OrderResult order={detailOrder} rawOrder={detailRawOrder} />
          ) : (
            <div className="detail-placeholder">{detailStatus}</div>
          )}
        </section>
      </section>
    </main>
  );
};

export default OrderLookupPage;
